import React, { useEffect, useState } from 'react';
import EntityTableHeader, { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import ForecastProductGrid from './ForecastProductGrid';
import { useProductGridData } from './serverProxy';
import EntityTable, { ENTITY_TABLE_PAGE_SIZE } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { ProductSalesDatum } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/types';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import {
  useForecastPeriod,
  useForecastType,
  useExportStartAndEndWeekIds,
  useForecastComparisonStartAndEndWeekIds,
  useLatestForecastModel
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/hooks';
import { shouldHidePagination } from 'src/components/BeaconV2Components/EntityTableRefresh/utils';
import { ForecastPeriod, ForecastType } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/types';
import { useAppSelector } from 'src/utils/Hooks';
import { useRetailerDisplayName } from 'src/utils/Hooks/reduxSelectors';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { getAppStage } from 'src/utils/app';
import { ExportConfig } from 'src/components/BeaconV2Components/common/AppExportModal/exportDataUtils';

const FETCH_CHUNK_SIZE = 100;
const ITEMS_PER_PAGE = 20;
const PAGES_PER_CHUNK = FETCH_CHUNK_SIZE / ITEMS_PER_PAGE;

/**
 * Handles displaying either the product grid or table and
 * handles switching between the two views.
 */
const ForecastProductListView = () => {
  const [page, setPage] = useState(1);
  const appStage = getAppStage();
  const forecastPeriod = useForecastPeriod();
  const { historicStartWeekId, historicEndWeekId, forecastEndWeekId, forecastStartWeekId } =
    useExportStartAndEndWeekIds();
  const forecastType = useForecastType();
  const retailerDisplayName = useRetailerDisplayName();
  const { startWeekId: comparisonStartWeekId, endWeekId: comparisonEndWeekId } =
    useForecastComparisonStartAndEndWeekIds();
  const { type: mainEntityType, name: clientName } = useAppSelector((state) => state.entityService.mainEntity);

  useEffect(() => {
    setPage(1);
  }, [forecastPeriod]);

  const chunkIndex = ((page - 1) % PAGES_PER_CHUNK) + 1;
  const chunkStartIndex = (chunkIndex - 1) * ITEMS_PER_PAGE;

  const { data: productGridData, isLoading: productGridDataLoading } = useProductGridData({
    page: Math.ceil(page / PAGES_PER_CHUNK),
    pageSize: FETCH_CHUNK_SIZE
  });
  const [listView, setListView] = useState<'tile' | 'table'>('tile');

  const handleChangeLayout = () => {
    setListView((oldView) => (oldView === 'table' ? 'tile' : 'table'));
  };

  const onClickNext = () => {
    const adjustedPage = ((page - 1) % PAGES_PER_CHUNK) + 1;
    const dataStartIndex = (adjustedPage - 1) * ITEMS_PER_PAGE;
    // If the last fetched page of data is less than the length of data we
    // can show, than there are no more pages to fetch
    if (
      !productGridDataLoading &&
      productGridData.slice(dataStartIndex, dataStartIndex + ITEMS_PER_PAGE).length < ITEMS_PER_PAGE
    ) {
      return;
    }
    setPage(page + 1);
  };

  const onClickPrevious = () => {
    setPage(Math.max(1, page - 1));
  };

  const retailerId = useAppSelector((state) => state.retailer.id);
  const { currentPublishVersion, modelVersion } = useLatestForecastModel();
  const removeRetailPriceUnadjusted = useRemoveRetailPriceRangeFilters('unadjusted-forecast');
  const removeRetailPriceAdjusted = useRemoveRetailPriceRangeFilters('adjusted-forecast');
  const applyEntityFilters = useApplyEntityFilters();

  const buildUnadjustedForecastExportRequest = ({
    groupByFieldName,
    includeCompTimePeriodForExport
  }: {
    groupByFieldName: string;
    includeCompTimePeriodForExport: boolean;
  }) => {
    const convertedForecastGroupBy = groupByFieldName.replace('weekId', 'forecastWeekId');
    const filename = `${mainEntityType}_${clientName}_forecasts_${forecastType}_${retailerDisplayName}_${
      includeCompTimePeriodForExport ? comparisonStartWeekId : historicStartWeekId
    }_${forecastEndWeekId}`;

    const aggregations = [
      {
        aggregateByFieldDisplayName: 'Total Traffic',
        aggregateByFieldName: 'totalTraffic',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Organic Traffic',
        aggregateByFieldName: 'organicTraffic',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Other Traffic',
        aggregateByFieldName: 'otherTraffic',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Paid Traffic',
        aggregateByFieldName: 'paidTrafficValue',
        func: 'sum',
        canBeExported: true
      },
      { aggregateByFieldDisplayName: 'Ad Spend', aggregateByFieldName: 'adSpend', func: 'sum', canBeExported: true },
      { aggregateByFieldDisplayName: 'Ad Sales', aggregateByFieldName: 'adSales', func: 'sum', canBeExported: true },
      {
        aggregateByFieldDisplayName: 'Weighted Content Score By Content Count',
        aggregateByFieldName: 'weightedContentScoreByContentScoreCount',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Content Score by Units',
        aggregateByFieldName: 'weightedContentScoreByUnits',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Units Sold For Content Score',
        aggregateByFieldName: 'unitsSoldForContentScore',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Content Score Count',
        aggregateByFieldName: 'contentScoreCount',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Star Rating By Number of Reviews',
        aggregateByFieldName: 'weightedRatingByRatingCount',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Buy Box Wins By Units',
        aggregateByFieldName: 'weightedBuyBoxWinRateByUnits',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted In-Stock Rate By Units',
        aggregateByFieldName: 'weightedInStockRateByUnits',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Number of Cumulative Reviews',
        aggregateByFieldName: 'ratingCount',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Number of New Reviews',
        aggregateByFieldName: 'incrementalReviewsFinal',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Retail Sales',
        aggregateByFieldName: 'retailSales',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Units Sold',
        aggregateByFieldName: 'unitsSold',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Wholesale Sales',
        aggregateByFieldName: 'wholesaleSales',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Retailer Margin',
        aggregateByFieldName: 'retailerMargin',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Brand Margin',
        aggregateByFieldName: 'brandMargin',
        func: 'sum',
        canBeExported: true
      }
    ];
    const unAdjustedRequestBuilder = new AdvancedSearchRequestBuilder(
      'unadjustedForecastByweekId_weekId-client',
      'beacon-unadjusted-forecast'
    );

    const unadjustedAggregationBuilder = new AggregationBuilder(convertedForecastGroupBy);

    aggregations.forEach(({ aggregateByFieldDisplayName, aggregateByFieldName, func, canBeExported }) => {
      unadjustedAggregationBuilder.addAggregationField(
        aggregateByFieldDisplayName,
        aggregateByFieldName,
        func,
        canBeExported
      );
    });

    unadjustedAggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);
    unadjustedAggregationBuilder.addConditionTermFilterWithCondition('publishVersion', 'must', [currentPublishVersion]);
    unadjustedAggregationBuilder.addConditionRangeFilter('forecastWeekId', forecastStartWeekId, forecastEndWeekId);
    unadjustedAggregationBuilder.setSortByAggregationField('Retail Sales', 'retailSales', 'sum', true);
    unadjustedAggregationBuilder.setSortDirection('desc');

    // If ForecastType is Adjusted, we should not include this into the request
    if (forecastType === ForecastType.UNADJUSTED) {
      unAdjustedRequestBuilder.setCustomAdditionalMetadata({
        toCompare: includeCompTimePeriodForExport,
        forecastSelection: forecastPeriod === ForecastPeriod.FULL_YEAR ? 'Full Year' : forecastPeriod
      });
    }

    unAdjustedRequestBuilder
      .setPageNumber(1)
      .setPageSize(1200)
      .addConditionRangeFilter('forecastWeekId', forecastStartWeekId, forecastEndWeekId)
      .addConditionTermFilter('retailerId', 'should', [retailerId])
      .addConditionTermFilter('forecastModelVersion', 'must', [modelVersion])
      .addConditionTermFilter('publishVersion', 'must', [currentPublishVersion])
      .addSortField('Retail Sales', 'retailSales', 'sum', true)
      .setPeriod('year')
      .setRetailerId(retailerId)
      .setDoAggregation(true)
      .setReturnDocuments(false)
      .setFileName(filename)
      .setSearchBy('parent')
      .apply(applyEntityFilters)
      .apply(removeRetailPriceUnadjusted)
      .addAggregation(unadjustedAggregationBuilder.build());

    const unadjustedForecastsRequest = unAdjustedRequestBuilder.build();
    return unadjustedForecastsRequest;
  };

  const buildAdjustedForecastExportRequest = ({
    groupByFieldName,
    includeCompTimePeriodForExport
  }: {
    groupByFieldName: string;
    includeCompTimePeriodForExport: boolean;
  }) => {
    const convertedForecastGroupBy = groupByFieldName.replace('weekId', 'forecastWeekId');
    const aggregations = [
      {
        aggregateByFieldDisplayName: 'Total Traffic Delta',
        aggregateByFieldName: 'totalTrafficDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Organic Traffic Delta',
        aggregateByFieldName: 'organicTrafficDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Other Traffic Delta',
        aggregateByFieldName: 'otherTrafficDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Paid Traffic Delta',
        aggregateByFieldName: 'paidTrafficDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Ad Spend Delta',
        aggregateByFieldName: 'adSpendDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Ad Sales Delta',
        aggregateByFieldName: 'adSalesDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Units Sold Delta',
        aggregateByFieldName: 'unitsSoldDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Retail Sales Delta',
        aggregateByFieldName: 'retailSalesDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Content Score By Content Count Delta',
        aggregateByFieldName: 'weightedContentScoreByContentScoreCountDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Content Score by Units Delta',
        aggregateByFieldName: 'weightedContentScoreByUnitsDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Units Sold For Content Score Delta',
        aggregateByFieldName: 'unitsSoldForContentScoreDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Content Score Count Delta',
        aggregateByFieldName: 'contentScoreDelta',
        func: 'avg',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Star Rating By Number of Reviews Delta',
        aggregateByFieldName: 'weightedRatingByRatingCountDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted Buy Box Wins By Units Delta',
        aggregateByFieldName: 'weightedBuyBoxWinRateByUnitsDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Weighted In-Stock Rate By Units Delta',
        aggregateByFieldName: 'weightedInStockRateByUnitsDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Number of New Reviews Delta',
        aggregateByFieldName: 'ratingCountDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Wholesale Sales Delta',
        aggregateByFieldName: 'wholesaleSalesDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Retailer Margin Delta',
        aggregateByFieldName: 'retailerMarginDelta',
        func: 'sum',
        canBeExported: true
      },
      {
        aggregateByFieldDisplayName: 'Brand Margin Delta',
        aggregateByFieldName: 'brandMarginDelta',
        func: 'sum',
        canBeExported: true
      }
    ];
    const adjustedRequestBuilder = new AdvancedSearchRequestBuilder(
      'adjustedForecastByweekId_weekId-client',
      'beacon-adjusted-forecast'
    );

    const adjustedAggregationBuilder = new AggregationBuilder(convertedForecastGroupBy);

    aggregations.forEach(({ aggregateByFieldDisplayName, aggregateByFieldName, func, canBeExported }) => {
      adjustedAggregationBuilder.addAggregationField(
        aggregateByFieldDisplayName,
        aggregateByFieldName,
        func,
        canBeExported
      );
    });

    adjustedAggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);
    adjustedAggregationBuilder.addConditionRangeFilter('forecastWeekId', forecastStartWeekId, forecastEndWeekId);
    adjustedAggregationBuilder.addConditionTermFilterWithCondition('publishVersion', 'must', [currentPublishVersion]);
    adjustedAggregationBuilder.addConditionTermFilterWithCondition('appStage', 'must', [appStage]);
    adjustedAggregationBuilder.setSortByAggregationField('Retail Sales Delta', 'retailSalesDelta', 'sum', true);
    adjustedAggregationBuilder.setSortDirection('desc');

    adjustedRequestBuilder
      .setCustomAdditionalMetadata({
        toCompare: includeCompTimePeriodForExport,
        forecastSelection: forecastPeriod === ForecastPeriod.FULL_YEAR ? 'Full Year' : forecastPeriod
      })
      .setPageNumber(1)
      .setPageSize(1200)
      .addConditionRangeFilter('forecastWeekId', forecastStartWeekId, forecastEndWeekId)
      .addConditionTermFilter('retailerId', 'should', [retailerId])
      .addConditionTermFilter('forecastModelVersion', 'must', [modelVersion])
      .addConditionTermFilter('publishVersion', 'must', [currentPublishVersion])
      .addConditionTermFilter('appStage', 'must', [appStage])
      .addSortField('Retail Sales Delta', 'retailSalesDelta', 'sum', true)
      .setPeriod('year')
      .setRetailerId(retailerId)
      .setDoAggregation(true)
      .setReturnDocuments(false)
      .setSearchBy('parent')
      .apply(applyEntityFilters)
      .apply(removeRetailPriceAdjusted)
      .addAggregation(adjustedAggregationBuilder.build());

    const adjustedForecastsRequest = adjustedRequestBuilder.build();
    return adjustedForecastsRequest;
  };

  return (
    <Flex flexDirection="column">
      <EntityTableHeader
        title="Retail Sales Projection by Product"
        enableSwitchingLayouts
        defaultView={listView}
        handleChangeLayout={handleChangeLayout}
        iconsList={[IconsList.export, IconsList.tile, IconsList.table]}
        buildExportRequest={({ originalRequest, groupByFieldName, includeCompTimePeriod }) => {
          // get the groupByFieldName from the modal dropdown
          const unadjustedExportRequest = buildUnadjustedForecastExportRequest({
            groupByFieldName,
            includeCompTimePeriodForExport: includeCompTimePeriod
          });
          const adjustedExportRequest = buildAdjustedForecastExportRequest({
            groupByFieldName,
            includeCompTimePeriodForExport: includeCompTimePeriod
          });
          const convertHistoricRequest = originalRequest.map((exportRequest) => {
            // this is a special case for the ratings and reviews forecasts export - won't happen in general export so added in here.
            if (exportRequest.searchType === 'beacon-ratings-reviews-metrics') {
              exportRequest.conditions.rangeFilters = [
                { fieldName: 'weekId', minValue: historicStartWeekId, maxValue: historicEndWeekId }
              ];
            }
            // override the filename to include the forecast week id
            exportRequest.fileName = `${mainEntityType}_${clientName}_forecasts_${forecastType}_${retailerDisplayName}_${
              includeCompTimePeriod ? comparisonStartWeekId : historicStartWeekId
            }_${forecastEndWeekId}`;
            return {
              ...exportRequest,
              exportResults: true,
              aggregations: [
                {
                  ...exportRequest.aggregations[0],
                  ...(includeCompTimePeriod && forecastPeriod === ForecastPeriod.FULL_YEAR
                    ? {
                        comparisonRangeFilters: [
                          { fieldName: 'weekId', maxValue: comparisonEndWeekId, minValue: comparisonStartWeekId }
                        ]
                      }
                    : {}),
                  conditions: {
                    ...exportRequest.aggregations[0].conditions,
                    rangeFilters: [{ fieldName: 'weekId', minValue: historicStartWeekId, maxValue: historicEndWeekId }]
                  }
                }
              ]
            };
          });

          return [
            ...(includeCompTimePeriod || forecastPeriod === ForecastPeriod.FULL_YEAR ? convertHistoricRequest : []),
            unadjustedExportRequest,
            ...(forecastType === ForecastType.ADJUSTED ? [adjustedExportRequest] : [])
          ] as ExportConfig[];
        }}
      />
      <Flex flexDirection="column" gap="md">
        {listView === 'tile' ? (
          <ForecastProductGrid productSalesData={productGridData} loading={productGridDataLoading} page={chunkIndex} />
        ) : (
          <EntityTable
            showPagination={false}
            isLoading={productGridDataLoading}
            shouldModifyColumnDefs={false}
            columnDefs={[
              {
                headerName: 'Products',
                valueFormatter: (_, row: ProductSalesDatum) => (
                  <ProductCell key={row.stacklineSku} stacklineSku={row.stacklineSku} title={row.productTitle} />
                )
              },
              {
                headerName: 'Retail Sales',
                valueFormatter: (_, row: ProductSalesDatum) => (
                  <MetricCell
                    value={row.retailSales_sum_value}
                    metricType={METRICTYPE.MONEY}
                    percentChange={row.retailSalesPercentChange}
                  />
                )
              },
              {
                headerName: 'Units Sold',
                valueFormatter: (_, row: ProductSalesDatum) => (
                  <MetricCell
                    value={row.unitsSold_sum_value}
                    metricType={METRICTYPE.VOLUME}
                    percentChange={row.unitsSoldPercentChange}
                  />
                )
              },
              {
                headerName: 'Total Traffic',
                valueFormatter: (_, row: ProductSalesDatum) => (
                  <MetricCell value={row.totalClicks_sum_value} metricType={METRICTYPE.VOLUME} />
                )
              },
              {
                field: 'conversionRate',
                headerName: 'Conversion Rate',
                valueFormatter: (_, row: ProductSalesDatum) => (
                  <MetricCell
                    value={row.conversionRate}
                    metricType={METRICTYPE.PERCENT}
                    percentChange={row.conversionRatePercentChange}
                  />
                )
              }
            ]}
            rowData={productGridData.slice(chunkStartIndex, chunkStartIndex + ITEMS_PER_PAGE)}
            initialPageSize={ITEMS_PER_PAGE}
          />
        )}
        {/* There is a margin on the table in stack ui, add negative margin to make it look right */}
        {shouldHidePagination({
          page,
          pageSize: ENTITY_TABLE_PAGE_SIZE,
          rowCount: (productGridData || []).length
        }) ? null : (
          <Flex justifyContent="flex-end" marginTop={listView === 'table' ? '-20px' : undefined}>
            <Pagination
              currentPage={page}
              shouldUseInfinitePagination
              onClickNext={onClickNext}
              onClickPrevious={onClickPrevious}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default ForecastProductListView;
